import React, { FC, useMemo } from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { BarChart, Bar, Cell, ReferenceLine, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip } from 'recharts';
import { Tooltip } from '../Tooltip';
import { MonthYearPicker } from '../MonthYearPicker';

// Custom tooltip for daily consumption chart
const DailyConsumptionTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload || !payload.length) return null;
  
  let dateText = "Invalid Date";
  try {
    // Get the payload data which contains gw_ts for gateway timezone date
    const payloadData = payload[0].payload;
    
    if (payloadData.gw_ts) {
      // Use the gateway timezone date directly
      const gwDate = new Date(payloadData.gw_ts);
      if (!isNaN(gwDate.getTime())) {
        dateText = gwDate.toLocaleDateString();
      }
    } else {
      // Fallback to day field if gw_ts is not available
      const dateValue = payloadData.day || label;
      const date = new Date(dateValue);
      if (!isNaN(date.getTime())) {
        dateText = date.toLocaleDateString();
      }
    }
  } catch (e) {
    // Keep the default "Invalid Date" text
  }
  
  // Find consumption and delivery values
  const consumptionData = payload.find((item: any) => item.dataKey === 'consumption');
  const deliveryData = payload.find((item: any) => item.dataKey === 'delivery');
  
  const consumption = consumptionData ? consumptionData.value : 0;
  const delivery = deliveryData ? Math.abs(deliveryData.value) : 0; // Convert back to positive for display
  
  return (
    <div className="bg-white/70 dark:bg-[#1D1D1D]/70 border border-gray-200/50 dark:border-[#292929]/50 rounded-[8px] p-3 shadow-lg max-w-[240px] whitespace-normal break-words backdrop-blur-sm">
      <p className="text-gray-500 dark:text-[#9CA3AF] text-sm font-medium mb-2">{dateText}</p>
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-green-500"></div>
          <span className="text-gray-700 dark:text-[#E5E7EB] text-sm">Consumption:</span>
          <span className="text-gray-900 dark:text-[#E5E7EB] font-medium">{consumption.toFixed(2)} kWh</span>
        </div>
        {delivery > 0 && (
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-blue-500"></div>
            <span className="text-gray-700 dark:text-[#E5E7EB] text-sm">Production:</span>
            <span className="text-gray-900 dark:text-[#E5E7EB] font-medium">{delivery.toFixed(2)} kWh</span>
          </div>
        )}
        <div className="pt-1 border-t border-gray-200 dark:border-[#292929]">
          <div className="flex items-center gap-2">
            <span className="text-gray-700 dark:text-[#E5E7EB] text-sm font-medium">Net:</span>
            <span className={`font-medium ${(consumption - delivery) > 0 ? 'text-orange-500' : 'text-green-500'}`}>
              {(consumption - delivery).toFixed(2)} kWh
            </span>
            <span className="text-gray-400 text-xs">
              {(consumption - delivery) > 0 ? '(consuming)' : '(producing)'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

interface MonthlyData {
  day: string;
  consumption: number;
  delivery: number;
  formattedDay: string;
  timestamp: number;
  originalTs: string | null;
  gw_ts: Date;
}

interface TimelineState {
  isLoadingBuffer: boolean;
}

interface DailyEnergyFlowChartProps {
  currentMonthData: MonthlyData[];
  selectedDay: {ts_utc: string, ts_gw: string, day_str: string} | null;
  selectedMonth: {year: number, month: number};
  onMonthChange: (month: {year: number, month: number}) => void;
  onDaySelect: (data: any) => void;
  timelineState: TimelineState;
  gatewayTimezone?: string;
}

export const DailyEnergyFlowChart: FC<DailyEnergyFlowChartProps> = ({
  currentMonthData,
  selectedDay,
  selectedMonth,
  onMonthChange,
  onDaySelect,
  timelineState,
  gatewayTimezone
}) => {
  // Calculate fixed Y-axis domain to prevent vertical animation during scrolling
  const getFixedYAxisDomain = useMemo(() => {
    const dataSource = currentMonthData;
    
    if (!dataSource || dataSource.length === 0) {
      return [-20, 20]; // Default symmetrical range
    }
    
    let maxAbsoluteValue = 0;
    
    dataSource.forEach(item => {
      const consumption = item.consumption || 0;
      const delivery = Math.abs(item.delivery) || 0; // Use positive value for calculation
      
      // Find the maximum absolute value between consumption and delivery
      maxAbsoluteValue = Math.max(maxAbsoluteValue, consumption, delivery);
    });
    
    // Add padding and make the axis symmetrical
    const padding = 1;
    const symmetricalMax = maxAbsoluteValue + padding;
    
    return [-symmetricalMax, symmetricalMax];
  }, [currentMonthData]);

  return (
    <div className="bg-[#1D1D1D] rounded-[8px] px-3 pt-3 pb-1 border border-[#292929]">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <h3 className="text-base font-semibold text-white font-poly-sans-median">
            Daily Energy Flow (kWh)
          </h3>
          <Tooltip text="Daily energy flow\nfor the selected month\n\nClick bars to view details">
            <InformationCircleIcon className="w-4 h-4 text-gray-400 cursor-pointer" />
          </Tooltip>
          {/* Daily chart legend indicators */}
          <div className="flex items-center gap-6 ml-4">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400 font-medium">Consumption:</span>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-purple-500"></div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400 font-medium">Production:</span>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Month/Year Selector */}
          <MonthYearPicker
            value={selectedMonth}
            onChange={onMonthChange}
            timezone={gatewayTimezone}
          />
          
          {timelineState.isLoadingBuffer && (
            <div className="w-4 h-4 border border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          )}
        </div>
      </div>
      
      <div className="h-64 select-none relative">
        {currentMonthData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={currentMonthData}
              margin={{ top: 5, right: 10, left: -20, bottom: 0 }}
              onClick={onDaySelect}
              maxBarSize={36}
              barCategoryGap="0%"
              barGap="-97%"
            >
              <defs>
                <linearGradient id="dailyConsumptionGradient" x1="0" y1="1" x2="0" y2="0">
                  <stop offset="0%" stopColor="rgba(156, 74, 215, 0.1)" />
                  <stop offset="80%" stopColor="rgba(156, 74, 215, 0.8)" />
                </linearGradient>
                <linearGradient id="dailyProductionGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="20%" stopColor="rgba(68, 164, 87, 0.1)" />
                  <stop offset="100%" stopColor="rgba(68, 164, 87, 0.8)" />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" vertical={false} />
              <XAxis 
                dataKey="formattedDay"
                stroke="#555"
                tick={(props) => {
                  const { x, y, payload } = props;
                  return (
                    <g transform={`translate(${x},${y})`}>
                      <text
                        x={0}
                        y={0}
                        dy={0}
                        textAnchor="middle"
                        fill="#555"
                        fontSize="10px"
                      >
                        {payload.value}
                      </text>
                    </g>
                  );
                }}
                height={50}
                tickMargin={10}
                interval="preserveStartEnd"
              />
              <YAxis 
                stroke="#555"
                tick={{ fill: '#555', fontSize: '11px' }}
                tickFormatter={(value) => `${Math.abs(value).toFixed(0)}`}
                domain={getFixedYAxisDomain}
              />
              <RechartsTooltip 
                content={<DailyConsumptionTooltip />}
                cursor={{ 
                  fill: 'currentColor',
                  fillOpacity: 0.1
                }}
              />
              {/* Consumption bars (positive values) */}
              <Bar 
                dataKey="consumption" 
                name="Consumption" 
                fill="url(#dailyConsumptionGradient)"
                stroke="#9C4AD7"
                strokeWidth={0.5}
                radius={[4, 4, 0, 0]}
                style={{ cursor: 'pointer' }}
                isAnimationActive={false}
                animationDuration={0}
              >
                {currentMonthData.map((entry, index) => {
                  // Handle missing data days (filled with zeros)
                  if (entry.consumption === 0 && entry.delivery === 0) {
                    return <Cell key={`cell-${index}`} fill="#374151" stroke="none" strokeWidth={0} />; // Gray for missing data
                  }
                  
                  // Highlight selected day
                  const isSelected = selectedDay && selectedDay.day_str === entry.day;
                  if (isSelected) {
                    return <Cell key={`cell-${index}`} fill="#3B82F6" stroke="#FFFFFF" strokeWidth={1} />;
                  }
                  
                  return <Cell key={`cell-${index}`} fill="url(#dailyConsumptionGradient)" stroke="#9C4AD7" strokeWidth={0.5} />;
                })}
              </Bar>
              {/* Production bars (negative values) */}
              <Bar 
                dataKey="delivery" 
                name="Production" 
                fill="url(#dailyProductionGradient)"
                stroke="#44A457"
                strokeWidth={0.5}
                radius={[4, 4, 0, 0]}
                style={{ cursor: 'pointer' }}
                isAnimationActive={false}
                animationDuration={0}
              >
                {currentMonthData.map((entry, index) => {
                  // Handle missing data days (filled with zeros)
                  if (entry.consumption === 0 && entry.delivery === 0) {
                    return <Cell key={`delivery-cell-${index}`} fill="#374151" stroke="none" strokeWidth={0} />; // Gray for missing data
                  }
                  
                  // Highlight selected day
                  const isSelected = selectedDay && selectedDay.day_str === entry.day;
                  if (isSelected) {
                    return <Cell key={`delivery-cell-${index}`} fill="#3B82F6" stroke="#FFFFFF" strokeWidth={1} />;
                  }
                  
                  return <Cell key={`delivery-cell-${index}`} fill="url(#dailyProductionGradient)" stroke="#44A457" strokeWidth={0.5} />;
                })}
              </Bar>
              {/* Subtle zero line for visual separation */}
              <ReferenceLine y={0} stroke="#333333" strokeWidth={1} />
            </BarChart>
          </ResponsiveContainer>
        ) : timelineState.isLoadingBuffer ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
              <p className="text-gray-400 text-sm">Loading timeline data...</p>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-full">
            <p className="text-gray-400 text-sm">No daily consumption data available</p>
          </div>
        )}
      </div>
    </div>
  );
}; 